import { css } from 'styled-components';

import { HeadingFontFamily } from '../../../merchantAdminTheme';

export type FontWeight = 'inherit' | 'normal' | 'semiBold' | 'bold';

const fontWeightMixin = ({ fontWeight = 'inherit' }: { fontWeight?: FontWeight }) => {
  switch (fontWeight) {
    case 'inherit':
      return 'inherit';
    case 'normal':
      return '400';
    case 'semiBold':
      return '500';
    case 'bold':
      return '700';
  }
};

const fontFeatureSettingsConfig: Record<HeadingFontFamily, string> = {
  'WoltHeading-Omnes': "'kern', 'ss01', 'ss05', 'ss07'",

  // 'ss01' intentionally not applied due to multiple October fonts and their possible differences
  'WoltHeading-October': "'kern'",

  // Enable same stylistic sets as for WoltHeading-Omnes to make the fallback Omnes look correct.
  // The expectation is that these stylistic sets won't affect Plus Rounded 1c itself.
  'WoltHeading-M-Plus-Rounded-1c': "'kern', 'ss01', 'ss05', 'ss07'",
};

const headingFontFamily = css`
  font-feature-settings: ${({ theme: { headingFontFamily } }) => fontFeatureSettingsConfig[headingFontFamily]};
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizelegibility;
  font-family:
    ${({ theme }) => theme.headingFontFamily},
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    Roboto,
    'Open Sans',
    'Helvetica Neue',
    sans-serif;
  font-variant-ligatures: common-ligatures;
`;

const baseFontFamily = css`
  font-feature-settings: 'kern';
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizelegibility;
  font-family:
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    Roboto,
    'Open Sans',
    'Helvetica Neue',
    sans-serif;
`;

const typographyCss = {
  baseFontFamily,
  Heading1: css`
    ${headingFontFamily};
    font-size: 48px;
    line-height: 52px;
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
  `,

  Heading2: css`
    ${headingFontFamily};
    font-size: 40px;
    line-height: 44px;
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
  `,
  Heading3: css`
    ${headingFontFamily};
    font-size: 32px;
    line-height: 36px;
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
  `,
  Heading4: css`
    ${headingFontFamily};
    font-size: 28px;
    line-height: 32px;
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
  `,
  Heading5: css`
    ${headingFontFamily};
    font-size: 24px;
    line-height: 28px;
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
  `,
  Heading6: css`
    ${headingFontFamily};
    font-size: 20px;
    line-height: 24px;
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
  `,
  Title1: css`
    ${baseFontFamily};
    font-size: 16px;
    line-height: 20px;
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
  `,
  Title2: css`
    ${baseFontFamily};
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
  `,
  Subtitle: css`
    ${baseFontFamily};
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
  `,
  Body1: css<{ fontWeight?: FontWeight }>`
    ${baseFontFamily};
    font-size: 18px;
    line-height: 24px;
    font-weight: ${fontWeightMixin};
    font-style: normal;
    font-stretch: normal;
  `,
  Body2: css<{ fontWeight?: FontWeight }>`
    ${baseFontFamily};
    font-size: 16px;
    line-height: 20px;
    font-weight: ${fontWeightMixin};
    font-style: normal;
    font-stretch: normal;
  `,
  Body3: css<{ fontWeight?: FontWeight }>`
    ${baseFontFamily};
    font-size: 14px;
    line-height: 20px;
    font-weight: ${fontWeightMixin};
    font-style: normal;
    font-stretch: normal;
  `,
  Small: css`
    ${baseFontFamily};
    font-size: 12px;
    line-height: 16px;
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
  `,
  Tiny: css`
    ${baseFontFamily};
    font-size: 10px;
    line-height: 12px;
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
  `,
  Overline1: css`
    ${baseFontFamily};
    text-transform: uppercase;
    font-size: 16px;
    line-height: 20px;
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
  `,
  Overline2: css`
    ${baseFontFamily};
    text-transform: uppercase;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
  `,
  Overline3: css`
    ${baseFontFamily};
    text-transform: uppercase;
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
  `,
  Overline4: css`
    ${baseFontFamily};
    text-transform: uppercase;
    font-size: 10px;
    line-height: 12px;
    font-weight: 500;
    font-style: normal;
    font-stretch: normal;
  `,
  Quote1: css`
    ${baseFontFamily};
    font-size: 16px;
    line-height: 20px;
    font-weight: 500;
    font-style: italic;
    font-stretch: normal;
  `,
  Quote2: css`
    ${baseFontFamily};
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    font-style: italic;
    font-stretch: normal;
  `,
};

export default typographyCss;
